import { cva, VariantProps } from 'class-variance-authority';
import { forwardRef, memo, ReactNode } from 'react';

import { cn } from '../../lib/styles';
import { Label } from '../Label';

const inputVariants = cva(
  'font-body-md font-medium text-text-primary placeholder:text-text-tertiary outline-1 outline-bg-inter-secondary-default focus-visible:outline-border-input-select transition-all',
  {
    variants: {
      variant: {
        default: 'bg-bg-inter-secondary-default rounded-sm',
        unstyled: 'border-none outline-none',
      },
      size: {
        sm: 'p-2 h-8',
        md: 'p-2 h-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'sm',
    },
  },
);

export type InputProps = Omit<React.ComponentProps<'input'>, 'size' | 'ref'> &
  VariantProps<typeof inputVariants> & {
    type?: string;
    label?: React.ReactNode;
    error?: React.ReactNode;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    containerClassName?: string;
    inputClassName?: string;
    leftIconClassName?: string;
    rightIconClassName?: string;
    component?: React.ComponentType;
    /**
     * Input mask pattern. Only works when an MaskInput component (e.g. from react-imask)
     * is passed as the `component` prop.
     */
    mask?: string;
  };

const InputBase = (
  {
    id,
    className,
    type,
    variant,
    size,
    label,
    error,
    leftIcon,
    rightIcon,
    containerClassName,
    inputClassName,
    leftIconClassName,
    rightIconClassName,
    component,
    ...rest
  }: InputProps,
  ref: React.ForwardedRef<HTMLInputElement>,
) => {
  const actualId = id || rest.name;

  const Component = component || 'input';

  const inputElement = (
    <div className={cn('relative flex items-center', containerClassName)}>
      {leftIcon && <div className={cn('absolute left-3 flex items-center', leftIconClassName)}>{leftIcon}</div>}

      <Component
        id={actualId}
        ref={ref}
        type={type}
        data-slot="input"
        className={cn(
          'placeholder:text-text-tertiary disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 w-full',
          inputVariants({ variant, size }),
          error && 'outline-text-destructive',
          leftIcon && 'pl-9',
          rightIcon && 'pr-8',
          label ? inputClassName : inputClassName || className,
        )}
        {...rest}
      />

      {rightIcon && <div className={cn('absolute right-3 flex items-center', rightIconClassName)}>{rightIcon}</div>}
    </div>
  );

  if (!label) return inputElement;

  return (
    <div className={cn('flex flex-col', className)}>
      <Label htmlFor={actualId} className="mb-2">
        {label}
      </Label>

      {inputElement}

      <div className="h-5 pt-1 text-text-destructive font-body-sm">{error}</div>
    </div>
  );
};

export const Input = memo(forwardRef(InputBase));
